import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faImage } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteAccountMediaByUrl, uploadAccountMedia } from 'api/media';
import { usePanelContext } from 'components/profile-panel/PanelContext';
import * as PanelPopover from 'components/profile-panel/PanelPopover';
import { ProfileItemTriggerKeys } from 'components/profile-panel/PanelPopoverButtons';
import { usePanelPopoverContext } from 'components/profile-panel/PanelPopoverContext';
import { Upload, UploadProps } from 'components/upload';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twMerge } from 'tailwind-merge';
import { GivebutterAxiosError } from 'types/error';

const ProfileCoverImage = () => {
    const { cover } = useCurrentAccount();
    const { placeholderCover, setPlaceholderCover } = usePanelContext();
    const [loadedPlaceholder, setLoadedPlaceholder] = useState(false);

    useEffect(() => {
        setLoadedPlaceholder(false);
    }, [placeholderCover]);

    return (
        <div className="group relative h-full max-h-40 w-full overflow-hidden rounded-t-xl bg-gray-200">
            {cover && (
                <img
                    src={cover.url}
                    alt="Your organization's cover"
                    draggable={false}
                    className="absolute h-full w-full bg-white object-cover"
                />
            )}
            {placeholderCover && (
                <img
                    alt=""
                    draggable={false}
                    src={URL.createObjectURL(placeholderCover)}
                    className={twMerge(
                        'absolute h-full w-full object-cover',
                        loadedPlaceholder && 'bg-white transition-all',
                    )}
                    onLoad={() => {
                        setLoadedPlaceholder(true);
                    }}
                />
            )}

            <PanelPopover.Root
                onOpenChange={open => {
                    if (!open) {
                        setPlaceholderCover(undefined);
                    }
                }}
            >
                <PanelPopover.Trigger asChild profileItem={ProfileItemTriggerKeys.CoverImageEdit}>
                    <Button
                        variant="outline"
                        color="neutral"
                        size="sm"
                        className={
                            'absolute right-2 top-2 items-center justify-center opacity-0 focus:opacity-100 group-hover:opacity-100'
                        }
                        aria-label="Update organization cover-image"
                    >
                        <FontAwesomeIcon icon={faImage} className="text-gray-400" />
                        Change cover
                    </Button>
                </PanelPopover.Trigger>
                <Content />
            </PanelPopover.Root>
        </div>
    );
};

const ProfileCoverIcon = () => {
    const { setPlaceholderCover, setDisableExpandButtonOnHover } = usePanelContext();
    return (
        <PanelPopover.Root
            onOpenChange={open => {
                if (!open) {
                    setPlaceholderCover(undefined);
                }
                setDisableExpandButtonOnHover(open);
            }}
        >
            <PanelPopover.Trigger asChild profileItem={ProfileItemTriggerKeys.CoverImageAdd}>
                <PanelPopover.IconExpandButton icon={faImage}>
                    Add cover
                </PanelPopover.IconExpandButton>
            </PanelPopover.Trigger>
            <Content />
        </PanelPopover.Root>
    );
};

const Content = () => {
    const { cover, accountId } = useCurrentAccount();
    const { placeholderCover, setPlaceholderCover, setDisableExpandButtonOnHover } =
        usePanelContext();
    const { close } = usePanelPopoverContext();
    const { success, error } = useToast();

    const { mutate: uploadImageForCover, isPending: isLoading } = useMutation({
        mutationFn: (file: File) => uploadAccountMedia(accountId, file, 'cover'),
        onSuccess: () => {
            success('Successfully uploaded cover image');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
            setDisableExpandButtonOnHover(false);
            close();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unable to upload cover image');
        },
    });

    const { mutate: deletePhoto, isPending: isDeleting } = useMutation<
        unknown,
        GivebutterAxiosError,
        { url: string; directRemoval?: boolean }
    >({
        mutationFn: ({ url }) => deleteAccountMediaByUrl(accountId, url),
        onSuccess: (_response, { directRemoval }) => {
            if (directRemoval) {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.current(accountId),
                });
                success('Removed cover photo');
                setPlaceholderCover(undefined);
                setDisableExpandButtonOnHover(false);
                close();
            }
        },
        onError: e => {
            error(e.response?.data?.message ?? 'Unable to clear cover photo');
        },
    });

    return (
        <PanelPopover.Content>
            <PanelPopover.Header>
                <PanelPopover.Title icon={faImage}>Upload cover image</PanelPopover.Title>
                {cover && (
                    <Button
                        variant="transparent"
                        color="neutral"
                        size="sm"
                        className="hover:bg-gray-100"
                        disabled={isDeleting}
                        onClick={() => {
                            if (cover) {
                                deletePhoto({ url: cover.url, directRemoval: true });
                            }
                        }}
                    >
                        {isDeleting ? 'Removing...' : 'Remove'}
                    </Button>
                )}
            </PanelPopover.Header>

            <div className="flex flex-col gap-4 p-4">
                <CoverUpload disabled={isDeleting} />

                <div className="flex justify-between">
                    <PanelPopover.CancelButton />
                    <Button
                        variant="solid"
                        size="sm"
                        disabled={placeholderCover === undefined || isLoading || isDeleting}
                        onClick={() => {
                            if (placeholderCover) {
                                uploadImageForCover(placeholderCover);
                                if (cover) {
                                    deletePhoto({ url: cover.url });
                                }
                            }
                        }}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </PanelPopover.Content>
    );
};

const CoverUpload = (props: UploadProps) => {
    const { cover } = useCurrentAccount();
    const { setPlaceholderCover } = usePanelContext();
    const { error } = useToast();

    return (
        <Upload
            variant="single"
            fullWidth
            labelStyle="gap-1 md:gap-1 py-4"
            defaultFiles={cover?.url ? [{ id: 'cover', url: cover.url }] : []}
            acceptedFileTypes={['image/*']}
            allowReorder={false}
            allowMultiple={false}
            allowReplace={true}
            allowRemove={false}
            onaddfile={(e, filePondFile) => {
                if (e) {
                    error(e?.body ?? 'Error adding cover image file');
                    return;
                }

                const { file } = filePondFile;
                setPlaceholderCover(file as File);
            }}
            {...props}
        />
    );
};

export { ProfileCoverImage, ProfileCoverIcon };
